import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { executeQuerySimple } from '../services/api';
import { getChartVisualization, generateChartsForNLPQuery } from '../services/api';
import { PlainQueryDisplay } from '../components/ui/PlainQueryDisplay';
import {
    ArrowLeft,
    Play,
    AlertCircle,
    Loader,
    Database,
    Table,
    TrendingUp
} from 'lucide-react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

// Chart color palettes
const CHART_COLORS = [
    '#75B7BA', '#848FA1', '#8CDAD6', '#90C38A', '#91BBE5',
    '#95D3E2', '#A6D976', '#B2E46B', '#BDBDBD', '#D6B0A7'
];

/**
 * QueryCompareView - Split view for comparing query executions with different parameters.
 * Flow:
 * 1. On mount, check if chart exists for the query
 * 2. If not, generate chart recommendations (blocking)
 * 3. Show parameter inputs once chart is ready
 * 4. Execute shows table + same chart type for both sides
 */
export const QueryCompareView = () => {
    const { t } = useTranslation(['page-query-compare', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { agentId, queryId } = useParams();
    const { showToast } = useToast();

    // Get query and database info from navigation state
    const query = location.state?.query;
    const databaseId = location.state?.databaseId;
    const agentName = location.state?.agentName || t('defaultAgentName');

    // State for two parameter sets
    const [parameters1, setParameters1] = useState({});
    const [parameters2, setParameters2] = useState({});
    const [errors1, setErrors1] = useState({});
    const [errors2, setErrors2] = useState({});

    // Execution states
    const [executing, setExecuting] = useState(false);
    const [execution1, setExecution1] = useState(null);
    const [execution2, setExecution2] = useState(null);

    // Chart states (loaded once during initialization)
    const [chartType, setChartType] = useState(null);
    const [chartConfig, setChartConfig] = useState(null);
    const [chartLoading, setChartLoading] = useState(true);
    const [chartError, setChartError] = useState(null);

    // Initialize parameters with default values
    useEffect(() => {
        if (!query && !location.state) {
            navigate(`/agents/queries/${agentId}`);
            return;
        }

        const defaultParams = {};
        if (query?.parameters) {
            query.parameters.forEach(param => {
                defaultParams[param.name] = param.default !== undefined ? param.default : '';
            });
        }
        setParameters1(defaultParams);
        setParameters2(defaultParams);
    }, [query, agentId, navigate, location.state]);

    // On mount: check if chart exists, if not generate it (non-blocking)
    useEffect(() => {
        if (queryId && databaseId) {
            initializeChart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryId, databaseId]);

    const initializeChart = async () => {
        setChartLoading(true);
        setChartError(null);
        try {
            // First, try to fetch existing chart
            try {
                const res = await getChartVisualization(databaseId, queryId);
                const data = res.data?.data;

                if (data?.chart_recommendations && data.chart_recommendations.length > 0) {
                    // Chart already exists, use it
                    const primaryChart = data.chart_recommendations[0];
                    setChartType(primaryChart.type);
                    setChartConfig(primaryChart.config);
                    setChartLoading(false);
                    return;
                }
            } catch (error) {
                // Chart doesn't exist, will generate below
                console.log('No existing chart found, generating...');
            }

            // Generate chart recommendations
            const genRes = await generateChartsForNLPQuery(databaseId, queryId);
            const genData = genRes.data?.data;

            if (genData?.chart_recommendations && genData.chart_recommendations.length > 0) {
                const primaryChart = genData.chart_recommendations[0];
                setChartType(primaryChart.type);
                setChartConfig(primaryChart.config);
            } else {
                console.warn('No chart recommendations in response');
                setChartError(t('chartNoRecommendations'));
            }
        } catch (error) {
            console.error('Failed to initialize chart:', error);
            setChartError(t('chartUnavailable'));
        } finally {
            setChartLoading(false);
        }
    };

    const handleParameterChange = (paramName, value, setParams, setErrs, errs) => {
        setParams(prev => ({
            ...prev,
            [paramName]: value
        }));
        if (errs[paramName]) {
            setErrs(prev => ({
                ...prev,
                [paramName]: null
            }));
        }
    };

    const validateParameters = (params, setErrs) => {
        const newErrors = {};
        if (query?.parameters) {
            query.parameters.forEach(param => {
                // Optional params (e.g. filters guarded by NULLIF/'' bypass in SQL) may be blank.
                if (param.required === false) return;
                const value = params[param.name];
                if (value === undefined || value === null || value.toString().trim() === '') {
                    newErrors[param.name] = t('fieldRequired', { name: param.name });
                }
            });
        }
        setErrs(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleExecuteBoth = async () => {
        // Validate both parameter sets
        const valid1 = validateParameters(parameters1, setErrors1);
        const valid2 = validateParameters(parameters2, setErrors2);
        
        if (!valid1 || !valid2 || executing) return;

        setExecuting(true);
        try {
            // Execute first parameter set
            showToast(t('executingSet1'), 'info');
            await executeWithParams(parameters1, setExecution1, 1);

            // Small delay between executions
            await new Promise(resolve => setTimeout(resolve, 500));

            // Execute second parameter set
            showToast(t('executingSet2'), 'info');
            await executeWithParams(parameters2, setExecution2, 2);

            showToast(t('bothCompleted'), 'success');
        } catch (error) {
            console.error('Execution failed:', error);
            showToast(t('executionFailed'), 'error');
        } finally {
            setExecuting(false);
        }
    };

    const executeWithParams = async (params, setExecution, execNum) => {
        try {
            const response = await executeQuerySimple({
                database_id: databaseId,
                query_id: queryId,
                parameters: params
            });
            if (response.data?.status === 'success') {
                // Store the response data directly (no AI analysis)
                setExecution({
                    execution: {
                        id: `exec-${execNum}`,
                        status: 'success',
                        row_count: response.data.data?.row_count || 0,
                        execution_duration_ms: response.data.data?.execution_duration_ms || 0,
                        columns: response.data.data?.columns || [],
                        data: response.data.data?.rows || response.data.data?.data || []
                    }
                });
            }
        } catch (error) {
            console.error('Failed to execute query:', error);
            throw error; // Re-throw to be caught by handleExecuteBoth
        }
    };

    const transformDataForChart = (data) => {
        if (!data || data.length === 0) return [];

        const transformRules = chartConfig?.transform_rules || {};
        const categoryField = transformRules.category_field;
        const valueField = transformRules.value_field;

        if (!categoryField || !valueField) {
            const fallbackKeys = Object.keys(data[0]);
            if (fallbackKeys.length >= 2) {
                return data.map(item => ({
                    category: String(item[fallbackKeys[0]]),
                    value: typeof item[fallbackKeys[1]] === 'number' ? item[fallbackKeys[1]] : parseFloat(item[fallbackKeys[1]]) || 0
                }));
            }
            return data;
        }

        const toNumber = (val) => {
            if (typeof val === 'number') return val;
            if (val === null || val === undefined) return 0;
            const num = parseFloat(String(val));
            return isNaN(num) ? 0 : num;
        };

        const formatCategory = (val) => {
            if (val === null || val === undefined) return 'N/A';
            if (typeof val === 'string') return val.replace(/\n/g, ' - ');
            if (val instanceof Date) return val.toLocaleDateString();
            return String(val);
        };

        return data.map(item => ({
            category: formatCategory(item[categoryField]),
            value: toNumber(item[valueField])
        }));
    };

    const renderChart = (data, execNum) => {
        if (!data || data.length === 0) {
            return (
                <div className="flex items-center justify-center h-64 text-gray-400">
                    <p>{t('executeQueryToDisplayChart')}</p>
                </div>
            );
        }

        if (!chartType) {
            return (
                <div className="flex items-center justify-center h-64 text-gray-400">
                    <p>{t('waitingForChartRecommendation')}</p>
                </div>
            );
        }

        const transformedData = transformDataForChart(data);
        const transformRules = chartConfig?.transform_rules || {};
        const categoryLabel = transformRules.category_label || t('categoryDefault');
        const valueLabel = transformRules.value_label || t('valueDefault');

        switch (chartType) {
            case 'bar':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={transformedData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }} />
                            <YAxis label={{ value: valueLabel, angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="value" fill={CHART_COLORS[execNum - 1]} name={valueLabel} />
                        </BarChart>
                    </ResponsiveContainer>
                );
            case 'line':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={transformedData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="category" label={{ value: categoryLabel, position: 'insideBottom', offset: -5 }} />
                            <YAxis label={{ value: valueLabel, angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="value" stroke={CHART_COLORS[execNum - 1]} name={valueLabel} />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'pie':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={transformedData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {transformedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );
            case 'table':
            default:
                const keys = Object.keys(data[0] || {});
                return (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {keys.map(key => (
                                        <th key={key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {key.replace(/_/g, ' ')}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {data.slice(0, 50).map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        {keys.map(key => (
                                            <td key={key} className="px-4 py-2 text-sm text-gray-600">
                                                {row[key] !== null ? String(row[key]) : 'NULL'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
        }
    };

    const extractResultData = (execData) => {
        if (!execData) return { columns: [], data: [] };
        
        const execution = execData.execution || {};
        return {
            columns: execution.columns || [],
            data: execution.data || []
        };
    };

    if (!query) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-12 h-12 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="max-w-[1800px] mx-auto pb-12">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('title')}</h1>
                        <p className="text-slate-500 mt-1">
                            {agentName} <span className="mx-2">•</span> {query.name}
                        </p>
                    </div>
                </div>
            </div>

            {/* Query Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-8">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h4 className="font-semibold text-gray-700 uppercase tracking-wide text-sm">{t('querySyntax')}</h4>
                </div>
                <div className="p-6">
                    <PlainQueryDisplay sql={query.query} />
                </div>
            </div>

            {/* Chart Status Banner */}
            <div className={`rounded-xl p-4 mb-8 flex items-center gap-3 ${
                chartError
                    ? 'bg-amber-50 border border-amber-200'
                    : chartLoading || !chartType
                    ? 'bg-primary-50 border border-primary-200'
                    : 'bg-green-50 border border-green-200'
            }`}>
                {chartError ? (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                ) : chartLoading || !chartType ? (
                    <Loader className="w-5 h-5 text-primary-600 animate-spin" />
                ) : (
                    <TrendingUp className="w-5 h-5 text-green-600" />
                )}
                <div>
                    {chartError ? (
                        <>
                            <p className="text-sm font-medium text-amber-900">{chartError}</p>
                            <p className="text-xs text-amber-700 mt-1">{t('tableStillDisplayed')}</p>
                        </>
                    ) : chartLoading || !chartType ? (
                        <>
                            <p className="text-sm font-medium text-primary-900">{t('loadingChartConfig')}</p>
                            <p className="text-xs text-primary-700 mt-1">{t('waitForChartLoad')}</p>
                        </>
                    ) : (
                        <>
                            <p className="text-sm font-medium text-green-900">
                                {t('chartTypeLabel')} <span className="capitalize">{chartType}</span>
                            </p>
                            <p className="text-xs text-green-700 mt-1">
                                {t('readyToExecute')}
                            </p>
                        </>
                    )}
                </div>
                {/* Execute Button */}
                <button
                    onClick={handleExecuteBoth}
                    disabled={executing || chartLoading || !chartType}
                    className={`ml-auto px-6 py-2.5 text-white rounded-xl font-semibold flex items-center gap-2 shadow-md transition-colors ${
                        executing || chartLoading || !chartType
                            ? 'bg-primary-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700'
                    }`}
                >
                    {executing ? (
                        <>
                            <Loader className="w-5 h-5 animate-spin" />
                            <span>{t('executing')}</span>
                        </>
                    ) : (
                        <>
                            <Play className="w-5 h-5 fill-current opacity-80" />
                            <span>{t('executeBoth')}</span>
                        </>
                    )}
                </button>
            </div>

            {/* Input Parameters Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Input Parameters 1 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50">
                        <h3 className="font-bold text-primary-900">{t('inputParameters1')}</h3>
                    </div>
                    <div className="p-6">
                        {query.parameters && query.parameters.length > 0 ? (
                            <div className="space-y-4">
                                {query.parameters.map((param, idx) => (
                                    <div key={idx}>
                                        <label className="block text-sm font-bold text-slate-800 mb-2 lowercase">
                                            {param.name.replace(/_/g, ' ')}
                                            {param.description && (
                                                <span className="block text-xs text-gray-400 mt-1 font-normal normal-case">
                                                    {param.description}
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                                            value={parameters1[param.name] || ''}
                                            onChange={(e) => handleParameterChange(param.name, e.target.value, setParameters1, setErrors1, errors1)}
                                            className={`w-full px-4 py-2.5 rounded-xl border bg-white focus:ring-2 focus:ring-primary-500 transition-all ${
                                                errors1[param.name]
                                                    ? 'border-red-300 ring-2 ring-red-100'
                                                    : 'border-gray-200'
                                            }`}
                                            placeholder={param.default || t('enterPlaceholder', { name: param.name })}
                                        />
                                        {errors1[param.name] && (
                                            <div className="flex items-center mt-2 text-sm text-red-600">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors1[param.name]}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">{t('noParametersRequired')}</p>
                        )}
                    </div>
                </div>

                {/* Input Parameters 2 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50">
                        <h3 className="font-bold text-primary-900">{t('inputParameters2')}</h3>
                    </div>
                    <div className="p-6">
                        {query.parameters && query.parameters.length > 0 ? (
                            <div className="space-y-4">
                                {query.parameters.map((param, idx) => (
                                    <div key={idx}>
                                        <label className="block text-sm font-bold text-slate-800 mb-2 lowercase">
                                            {param.name.replace(/_/g, ' ')}
                                            {param.description && (
                                                <span className="block text-xs text-gray-400 mt-1 font-normal normal-case">
                                                    {param.description}
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                                            value={parameters2[param.name] || ''}
                                            onChange={(e) => handleParameterChange(param.name, e.target.value, setParameters2, setErrors2, errors2)}
                                            className={`w-full px-4 py-2.5 rounded-xl border bg-white focus:ring-2 focus:ring-primary-500 transition-all ${
                                                errors2[param.name]
                                                    ? 'border-red-300 ring-2 ring-red-100'
                                                    : 'border-gray-200'
                                            }`}
                                            placeholder={param.default || t('enterPlaceholder', { name: param.name })}
                                        />
                                        {errors2[param.name] && (
                                            <div className="flex items-center mt-2 text-sm text-red-600">
                                                <AlertCircle className="w-4 h-4 mr-1" />
                                                {errors2[param.name]}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">{t('noParametersRequired')}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Results Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Table Result 1 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50 flex items-center gap-2">
                        <Table className="w-4 h-4 text-primary-600" />
                        <h4 className="font-bold text-primary-900 text-sm">{t('tableResult1')}</h4>
                        {executing && (
                            <span className="ml-auto text-xs text-primary-600 font-medium flex items-center gap-1">
                                <Loader className="w-3 h-3 animate-spin" />
                                {t('executing')}
                            </span>
                        )}
                        {execution1 && !executing && (
                            <span className="ml-auto text-xs text-primary-600 font-medium">
                                {t('rowsCount', { count: execution1.execution?.row_count || 0 })}
                            </span>
                        )}
                    </div>
                    <div className="p-4 overflow-x-auto max-h-96">
                        {executing && !execution1 ? (
                            <div className="flex items-center justify-center h-48 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('executingSet1')}</p>
                                </div>
                            </div>
                        ) : execution1 ? (
                            (() => {
                                const { columns, data } = extractResultData(execution1);
                                if (!columns || columns.length === 0) {
                                    return (
                                        <div className="text-center py-8 text-gray-400">
                                            <Database className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                            <p className="text-sm">{t('noDataReturned')}</p>
                                        </div>
                                    );
                                }
                                return (
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {columns.map((col, idx) => (
                                                    <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        {col}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {data.slice(0, 50).map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    {columns.map((col, colIdx) => (
                                                        <td key={colIdx} className="px-4 py-2 text-sm text-gray-600">
                                                            {row[col] !== null ? String(row[col]) : <span className="text-gray-300 italic">NULL</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })()
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                <p className="text-sm">{t('clickExecuteBothToRun', { button: t('executeBoth') })}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Result 2 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50 flex items-center gap-2">
                        <Table className="w-4 h-4 text-primary-600" />
                        <h4 className="font-bold text-primary-900 text-sm">{t('tableResult2')}</h4>
                        {executing && execution1 && (
                            <span className="ml-auto text-xs text-primary-600 font-medium flex items-center gap-1">
                                <Loader className="w-3 h-3 animate-spin" />
                                {t('executing')}
                            </span>
                        )}
                        {execution2 && !executing && (
                            <span className="ml-auto text-xs text-primary-600 font-medium">
                                {t('rowsCount', { count: execution2.execution?.row_count || 0 })}
                            </span>
                        )}
                    </div>
                    <div className="p-4 overflow-x-auto max-h-96">
                        {executing && execution1 && !execution2 ? (
                            <div className="flex items-center justify-center h-48 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('executingSet2')}</p>
                                </div>
                            </div>
                        ) : execution2 ? (
                            (() => {
                                const { columns, data } = extractResultData(execution2);
                                if (!columns || columns.length === 0) {
                                    return (
                                        <div className="text-center py-8 text-gray-400">
                                            <Database className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                            <p className="text-sm">{t('noDataReturned')}</p>
                                        </div>
                                    );
                                }
                                return (
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {columns.map((col, idx) => (
                                                    <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                        {col}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {data.slice(0, 50).map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    {columns.map((col, colIdx) => (
                                                        <td key={colIdx} className="px-4 py-2 text-sm text-gray-600">
                                                            {row[col] !== null ? String(row[col]) : <span className="text-gray-300 italic">NULL</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })()
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                <p className="text-sm">{t('clickExecuteBothToRun', { button: t('executeBoth') })}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Graph Results Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Graph Result 1 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary-600" />
                        <h4 className="font-bold text-primary-900 text-sm">{t('graphResult1')}</h4>
                    </div>
                    <div className="p-6">
                        {executing && !execution1 ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('waitingForExecution')}</p>
                                </div>
                            </div>
                        ) : execution1 && chartType ? (
                            renderChart(extractResultData(execution1).data, 1)
                        ) : chartLoading ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('loadingChart')}</p>
                                </div>
                            </div>
                        ) : chartError || !chartConfig ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <AlertCircle className="w-8 h-8 mx-auto mb-3 text-amber-500" />
                                    <p className="text-sm">{t('chartNotAvailable')}</p>
                                    <p className="text-xs mt-1">{t('tableStillAboveNote')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <p>{t('clickExecuteBothToDisplay', { button: t('executeBoth') })}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Graph Result 2 */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-primary-50/50 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary-600" />
                        <h4 className="font-bold text-primary-900 text-sm">{t('graphResult2')}</h4>
                    </div>
                    <div className="p-6">
                        {executing && !execution2 ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('waitingForExecution')}</p>
                                </div>
                            </div>
                        ) : execution2 && chartType ? (
                            renderChart(extractResultData(execution2).data, 2)
                        ) : chartLoading ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
                                    <p className="text-sm">{t('loadingChart')}</p>
                                </div>
                            </div>
                        ) : chartError || !chartConfig ? (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <div className="text-center">
                                    <AlertCircle className="w-8 h-8 mx-auto mb-3 text-amber-500" />
                                    <p className="text-sm">{t('chartNotAvailable')}</p>
                                    <p className="text-xs mt-1">{t('tableStillAboveNote')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-64 text-gray-400">
                                <p>{t('clickExecuteBothToDisplay', { button: t('executeBoth') })}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
